/**
 * StartPageNotice — §7.9.9 (POLICY_NAVIGATION_RAILS): when the Start Page
 * pref points at a community that is gone or dissolved and the resolver
 * lands elsewhere, tell the user ONCE — "Your start page pointed at X,
 * which is no longer active. Now starting at Y." Dismissible, one-time
 * per (user, stale value): a localStorage marker (user-namespaced per the
 * DEV-1443 rule) is written on first render, so it never becomes a
 * recurring nag; silent swallowing is equally forbidden.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { COMMUNITIES_BROWSE_ROUTE } from '../../Data/Membership/defaultCommunityLanding';
import styles from './StartPageNotice.module.css';

interface Props {
  userGuid: string;
  /** The stale pref value (community guid) — null renders nothing. */
  stalePrefGuid: string | null;
  /** Where the resolver actually landed. */
  landedRoute: string;
  /** Community entities, for name resolution (either casing). */
  entities: Array<Record<string, any>> | undefined;
}

const seenKey = (userGuid: string, staleGuid: string) =>
  `l4e_start_page_stale_seen:${userGuid.toUpperCase()}:${staleGuid.toUpperCase()}`;

const StartPageNotice: React.FC<Props> = ({ userGuid, stalePrefGuid, landedRoute, entities }) => {
  const { t } = useAppTranslation('pages');
  const [visible, setVisible] = useState(false);

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entities || []) {
      const g = String(e?.entity_guid || e?.entityGuid || '').toUpperCase();
      const n = String(e?.name || '').trim();
      if (g && n) map.set(g, n);
    }
    return (guid: string): string | null => map.get(guid.toUpperCase()) || null;
  }, [entities]);

  // Show once per (user, stale value); the marker is written at first
  // render so a reload doesn't re-nag even if the user never clicked ×.
  useEffect(() => {
    if (!userGuid || !stalePrefGuid) {
      setVisible(false);
      return;
    }
    try {
      const key = seenKey(userGuid, stalePrefGuid);
      if (window.localStorage.getItem(key) === '1') return;
      window.localStorage.setItem(key, '1');
      setVisible(true);
    } catch {
      /* privacy mode — show without persistence */
      setVisible(true);
    }
  }, [userGuid, stalePrefGuid]);

  if (!visible || !stalePrefGuid) return null;

  const staleName =
    nameOf(stalePrefGuid) || t('startPage.staleNotice.unknownCommunity');
  const landedGuid = landedRoute.startsWith('/communities/')
    ? landedRoute.slice('/communities/'.length)
    : null;
  const destName =
    landedRoute === COMMUNITIES_BROWSE_ROUTE || !landedGuid
      ? t('startPage.staleNotice.destBrowse')
      : nameOf(landedGuid) || t('startPage.staleNotice.destBrowse');

  return (
    <div className={styles.notice} role="status">
      <span className={styles.text}>
        {t('startPage.staleNotice.body', { name: staleName, dest: destName })}
      </span>
      <button
        type="button"
        className={styles.close}
        onClick={() => setVisible(false)}
        aria-label={t('startPage.staleNotice.dismiss')}
      >
        ×
      </button>
    </div>
  );
};

export default StartPageNotice;
